import {Component, Input, HostBinding, OnInit, Output, EventEmitter, ChangeDetectionStrategy, OnDestroy, ViewChild, ElementRef, Renderer2, ChangeDetectorRef} from '@angular/core';
import {DomSanitizer, SafeHtml, SafeStyle} from '@angular/platform-browser';
import {animate, style, transition, trigger} from '@angular/animations';
import {BehaviorSubject} from 'rxjs';

export interface Coord {
    x: number;
    y: number;
}

@Component({
    standalone: false,
    selector: 'gallery-image',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({opacity: 0}),
                animate('300ms ease-in', style({opacity: 1}))
            ])
        ])
    ],
    template: `
        <ng-container [lazyImage]="src"
                      (progress)="onProgress($event)"
                      (loaded)="onLoaded($event)"
                      (error)="onError($event)"
                      [ngSwitch]="state | async">
            <ng-container *ngIf="!isThumbnail">
                <div *ngSwitchCase="'success'" @fadeIn #zoomContainer class="g-image-item zoomContainer">
                    <img #imageThumbnail class="zoomThumbnail" [src]="src" style="width:100%;height:100%;object-fit:contain"/>
                    <div [ngClass]="{'zoomFullContainer': true, 'zoomLensEnabled': this.enableLens}"
                         [style.display]="this.display"
                         [style.top.px]="this.lensTop"
                         [style.left.px]="this.lensLeft"
                         [style.width.px]="this.lensWidth"
                         [style.height.px]="this.lensHeight"
                         [style.border-radius.px]="this.lensBorderRadius">
                        <img #fullSizeImage class="imageZoom" [src]="src"
                             [style.display]="this.display"
                             [style.top.px]="this.zoomImageTop"
                             [style.left.px]="this.zoomImageLeft"
                             [style.width.px]="this.magnifiedWidth"
                             [style.height.px]="this.magnifiedHeight"/>
                    </div>
                </div>
            </ng-container>
            <ng-container *ngIf="isThumbnail">
                <div *ngSwitchCase="'success'" @fadeIn class="g-image-item zoomContainer">
                    <img [src]="src" style="width:100%;height:100%;object-fit:contain"/>
                </div>
            </ng-container>
            <div *ngSwitchCase="'failed'"
                 class="g-image-error-message">
                <div *ngIf="errorTemplate; else defaultError"
                     [innerHTML]="errorTemplate"></div>
                <ng-template #defaultError>
                    <ng-container *ngIf="isThumbnail; else isLarge">
                        <h4>⚠</h4>
                    </ng-container>
                    <ng-template #isLarge>
                        <h2>⚠</h2>
                        <p>Unable to load the image!</p>
                    </ng-template>
                </ng-template>
            </div>
            <ng-container *ngSwitchCase="'loading'">
                <div *ngIf="loaderTemplate; else defaultLoader"
                     class="g-loading"
                     [innerHTML]="loaderTemplate">
                </div>
                <ng-template #defaultLoader>
                    <div *ngIf="isThumbnail" class="g-thumb-loading"></div>
                </ng-template>
            </ng-container>
        </ng-container>
    `
})

export class GalleryImageComponent implements OnInit, OnDestroy {

    /** Stream that emits the state */
    private readonly _state = new BehaviorSubject<'loading' | 'success' | 'failed'>('loading');
    readonly state = this._state.asObservable();

    /** Progress value */
    progress = 0;

    /** Is thumbnail */
    @Input() isThumbnail: boolean;

    /** Image source URL */
    @Input() src: string;
    /** Loaded image URL */
    imageUrl: SafeStyle;

    /** Custom loader template */
    @Input() loadingIcon: string;
    /** Custom loader safe template */
    loaderTemplate: SafeHtml;

    /** Custom error template */
    @Input() loadingError: string;
    /** Custom error safe template */
    errorTemplate: SafeHtml;

    /** Stream that emits when an error occurs */
    @Output() error = new EventEmitter<Error>();
    /** loading error */
    loadError: Error;

    @HostBinding('class.g-image-loaded') get imageLoadSuccess(): boolean {
        return !!this.imageUrl;
    }

    @HostBinding('class.g-image-error') get imageLoadFailed(): boolean {
        return !!this.loadError;
    }

    @ViewChild('zoomContainer', {static: false}) zoomContainer: ElementRef;
    @ViewChild('imageThumbnail', {static: false}) imageThumbnail: ElementRef;
    @ViewChild('fullSizeImage', {static: false}) fullSizeImage: ElementRef;
    public display: string;
    public zoomImageTop: number;
    public zoomImageLeft: number;
    public magnifiedWidth: number;
    public magnifiedHeight: number;
    public lensTop: number;
    public lensLeft: number;
    public enableLens = true;
    public lensBorderRadius = 0;
    public imgWidth: number;
    public imgHeight: number;
    public zoomWidth: number;
    public zoomHeight: number;
    public lensWidth = 200;
    public lensHeight = 200;
    private magnification = 2;
    private circularLens = true;
    private baseRatio: number;
    private minZoomRatio;
    private maxZoomRatio = 2;
    private xRatio: number;
    private yRatio: number;
    private offsetLeft: number;
    private offsetTop: number;
    private zoomingEnabled = false;
    private zoomFrozen = false;
    private latestMouseLeft: number;
    private latestMouseTop: number;
    private eventListeners: (() => void)[] = [];

    constructor(private _sanitizer: DomSanitizer, private renderer: Renderer2, private cdRef: ChangeDetectorRef) {
    }

    ngOnInit() {
        if (this.loadingIcon) {
            this.loaderTemplate = this._sanitizer.bypassSecurityTrustHtml(this.loadingIcon);
        }
        if (this.loadingError) {
            this.errorTemplate = this._sanitizer.bypassSecurityTrustHtml(this.loadingError);
        }
    }

    ngOnDestroy() {
        this._state.complete();
        this.eventListeners.forEach((destroyFn) => destroyFn());
    }

    onProgress({loaded, total}: { loaded: number, total: number }) {
        this.progress = loaded * 100 / total;
    }

    onLoaded(blobUrl: string) {
        this.imageUrl = this._sanitizer.bypassSecurityTrustStyle(`url("${blobUrl}")`);
        this._state.next('success');
        if (!this.isThumbnail) {
            this.cdRef.detectChanges();
            this.setUpEventListeners();
        }
    }

    onError(err: Error) {
        this.loadError = err;
        this._state.next('failed');
        this.error.emit(err);
    }

    private setUpEventListeners() {
        /*
        this.eventListeners.push(
            this.renderer.listen(this.zoomContainer.nativeElement, 'mouseenter', (event) => this.hoverMouseEnter(event))
        );
        */
        this.eventListeners.push(
            this.renderer.listen(this.zoomContainer.nativeElement, 'mouseleave', () => this.hoverMouseLeave())
        );
        this.eventListeners.push(
            this.renderer.listen(this.zoomContainer.nativeElement, 'mousemove', (event) => this.hoverMouseMove(event))
        );
        this.eventListeners.push(
            this.renderer.listen(this.zoomContainer.nativeElement, 'click', (event) => this.toggleClick(event))
        );
        if (this.enableLens && this.circularLens) {
            this.lensBorderRadius = this.lensWidth / 2;
        }
    }

    private toggleClick(event: MouseEvent) {
        if (this.zoomingEnabled) {
            this.zoomOff();
        } else {
            this.zoomOn(event);
        }
    }

    private hoverMouseEnter(event: MouseEvent) {
        this.zoomOn(event);
    }

    private hoverMouseLeave() {
        this.zoomOff();
    }

    private hoverMouseMove(event: MouseEvent) {
        if (this.zoomingEnabled) {
            this.calculateZoomPosition(event);
        }
    }

    private setZoomPosition(left: number, top: number) {
        this.latestMouseLeft = Number(left) || this.latestMouseLeft;
        this.latestMouseTop = Number(top) || this.latestMouseTop;
        const c: Coord = {
            x: this.latestMouseLeft,
            y: this.latestMouseTop
        };
    }

    private zoomOn(event: MouseEvent) {
        this.zoomingEnabled = true;
        this.calculateRatioAndOffset();
        this.display = 'block';
        this.calculateZoomPosition(event);
    }

    private zoomOff() {
        this.zoomingEnabled = false;
        this.display = 'none';
        this.cdRef.detectChanges();
    }

    private calculateZoomPosition(event: MouseEvent) {
        const newLeft = Math.max(Math.min(event.offsetX, this.imgWidth), 0);
        const newTop = Math.max(Math.min(event.offsetY, this.imgHeight), 0);
        this.setZoomPosition(newLeft, newTop);
        this.calculateImageAndLensPosition();
        this.cdRef.detectChanges();
    }

    private calculateImageAndLensPosition() {
        let lensLeftMod = 0;
        let lensTopMod = 0;

        if (this.enableLens) {
            lensLeftMod = this.lensLeft = this.latestMouseLeft - this.lensWidth / 2;
            lensTopMod = this.lensTop = this.latestMouseTop - this.lensHeight / 2;
        }

        this.zoomImageLeft = (this.latestMouseLeft * -this.xRatio) - lensLeftMod;
        this.zoomImageTop = (this.latestMouseTop * -this.yRatio) - lensTopMod;
    }

    private calculateRatioAndOffset() {
        this.imgWidth = this.imageThumbnail.nativeElement.width;
        this.imgHeight = this.imageThumbnail.nativeElement.height;

        // If lens is disabled, set lens size to equal thumb size and position it on top of the thumb
        if (!this.enableLens) {
            this.lensWidth = this.imgWidth;
            this.lensHeight = this.imgHeight;
            this.lensLeft = 0;
            this.lensTop = 0;
        }

        this.offsetTop = this.imageThumbnail.nativeElement.getBoundingClientRect().top;
        this.offsetLeft = this.imageThumbnail.nativeElement.getBoundingClientRect().left;

        this.zoomWidth = this.fullSizeImage.nativeElement.naturalWidth;
        this.zoomHeight = this.fullSizeImage.nativeElement.naturalHeight;

        this.baseRatio = Math.max(
            (this.imgWidth / this.zoomWidth),
            (this.imgHeight / this.zoomHeight));
        // Don't allow zooming to smaller than thumbnail size
        this.minZoomRatio = Math.max(this.minZoomRatio || 0, this.baseRatio || 0);
        this.calculateRatio();

    }

    private calculateRatio() {
        this.magnifiedWidth = (this.zoomWidth * this.magnification);
        this.magnifiedHeight = (this.zoomHeight * this.magnification);
        this.xRatio = (this.magnifiedWidth - this.imgWidth) / this.imgWidth;
        this.yRatio = (this.magnifiedHeight - this.imgHeight) / this.imgHeight;
    }
}
